/**
 * Quaternion tuple helpers. Tuples are `[x, y, z, w]` (three.js component order) and are
 * expected to be normalized. Pure functions; no DOM, no editor state.
 */

import { Quaternion } from 'three';
import type { QuaternionTuple } from './types';

const QUAT_EPS = 1e-3;

export function identityQuat(): QuaternionTuple {
  return [0, 0, 0, 1];
}

export function cloneQuat(q: QuaternionTuple): QuaternionTuple {
  return [q[0], q[1], q[2], q[3]];
}

export function quatLength(q: QuaternionTuple): number {
  return Math.hypot(q[0], q[1], q[2], q[3]);
}

/** Returns a normalized copy (or writes into `out`). A zero-length quaternion becomes identity. */
export function normalizeQuat(q: QuaternionTuple, out?: QuaternionTuple): QuaternionTuple {
  const target = out ?? ([0, 0, 0, 1] as QuaternionTuple);
  const len = quatLength(q);
  if (!Number.isFinite(len) || len === 0) {
    target[0] = 0;
    target[1] = 0;
    target[2] = 0;
    target[3] = 1;
    return target;
  }
  if (Math.abs(len - 1) < 1e-12) {
    // Already unit length: copy verbatim so repeated normalization is bit-for-bit idempotent
    // (clipOps and librarySchema normalize on every write; drift would break re-serialization).
    target[0] = q[0];
    target[1] = q[1];
    target[2] = q[2];
    target[3] = q[3];
    return target;
  }
  const inv = 1 / len;
  target[0] = q[0] * inv;
  target[1] = q[1] * inv;
  target[2] = q[2] * inv;
  target[3] = q[3] * inv;
  return target;
}

/** 4 finite numbers whose length is within 1e-3 of 1. */
export function isValidQuatTuple(x: unknown): x is QuaternionTuple {
  if (!Array.isArray(x) || x.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    const v: unknown = x[i];
    if (typeof v !== 'number' || !Number.isFinite(v)) return false;
  }
  const len = Math.hypot(x[0] as number, x[1] as number, x[2] as number, x[3] as number);
  return Math.abs(len - 1) < QUAT_EPS;
}

/**
 * Shortest-path spherical interpolation. `b` is negated when `dot(a, b) < 0` so interpolation
 * never takes the long way round (quaternion double cover).
 */
export function slerpTuple(
  a: QuaternionTuple,
  b: QuaternionTuple,
  t: number,
  out?: QuaternionTuple,
): QuaternionTuple {
  const target = out ?? ([0, 0, 0, 1] as QuaternionTuple);

  if (t <= 0) {
    target[0] = a[0];
    target[1] = a[1];
    target[2] = a[2];
    target[3] = a[3];
    return target;
  }

  let bx = b[0];
  let by = b[1];
  let bz = b[2];
  let bw = b[3];

  let cos = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
  if (cos < 0) {
    cos = -cos;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }

  if (t >= 1) {
    target[0] = bx;
    target[1] = by;
    target[2] = bz;
    target[3] = bw;
    return target;
  }

  let s0: number;
  let s1: number;
  if (cos > 1 - 1e-6) {
    // Nearly parallel: fall back to normalized linear interpolation.
    s0 = 1 - t;
    s1 = t;
  } else {
    const theta = Math.acos(Math.min(1, cos));
    const sinTheta = Math.sin(theta);
    s0 = Math.sin((1 - t) * theta) / sinTheta;
    s1 = Math.sin(t * theta) / sinTheta;
  }

  target[0] = a[0] * s0 + bx * s1;
  target[1] = a[1] * s0 + by * s1;
  target[2] = a[2] * s0 + bz * s1;
  target[3] = a[3] * s0 + bw * s1;
  return normalizeQuat(target, target);
}

export function quatFromThree(q: Quaternion, out?: QuaternionTuple): QuaternionTuple {
  const target = out ?? ([0, 0, 0, 1] as QuaternionTuple);
  target[0] = q.x;
  target[1] = q.y;
  target[2] = q.z;
  target[3] = q.w;
  return target;
}

export function quatToThree(q: QuaternionTuple, out?: Quaternion): Quaternion {
  const target = out ?? new Quaternion();
  return target.set(q[0], q[1], q[2], q[3]);
}

/** Hamilton product `a * b` (apply b first, then a — three.js convention). */
export function quatMultiply(
  a: QuaternionTuple,
  b: QuaternionTuple,
  out?: QuaternionTuple,
): QuaternionTuple {
  const target = out ?? ([0, 0, 0, 1] as QuaternionTuple);
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  const x = aw * bx + ax * bw + ay * bz - az * by;
  const y = aw * by - ax * bz + ay * bw + az * bx;
  const z = aw * bz + ax * by - ay * bx + az * bw;
  const w = aw * bw - ax * bx - ay * by - az * bz;
  target[0] = x;
  target[1] = y;
  target[2] = z;
  target[3] = w;
  return target;
}

/** Inverse (conjugate of the normalized quaternion). */
export function quatInvert(q: QuaternionTuple, out?: QuaternionTuple): QuaternionTuple {
  const target = out ?? ([0, 0, 0, 1] as QuaternionTuple);
  const lenSq = q[0] * q[0] + q[1] * q[1] + q[2] * q[2] + q[3] * q[3];
  if (!Number.isFinite(lenSq) || lenSq === 0) {
    target[0] = 0;
    target[1] = 0;
    target[2] = 0;
    target[3] = 1;
    return target;
  }
  const inv = 1 / lenSq;
  target[0] = -q[0] * inv;
  target[1] = -q[1] * inv;
  target[2] = -q[2] * inv;
  target[3] = q[3] * inv;
  return target;
}

/**
 * True when `a` and `b` describe the same rotation within `eps`. Double-cover aware: `q` and
 * `-q` are the same rotation and therefore compare equal.
 */
export function quatEquals(a: QuaternionTuple, b: QuaternionTuple, eps = 1e-6): boolean {
  let same = true;
  let flipped = true;
  for (let i = 0; i < 4; i++) {
    if (Math.abs(a[i] - b[i]) > eps) same = false;
    if (Math.abs(a[i] + b[i]) > eps) flipped = false;
  }
  return same || flipped;
}

/** Shortest-arc angle between two rotations, in radians (0..π). */
export function angleBetween(a: QuaternionTuple, b: QuaternionTuple): number {
  const dot = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]);
  return 2 * Math.acos(Math.min(1, Math.max(-1, dot)));
}
